// =============================================================================
// StudioHeader — jeden rzad: strzalka wstecz, domena (mono), status, silnik·motyw;
// pod spodem linki: pages.dev ↗ · preview ↗ (ostatni udany deploy branch preview)
// · mapa · komunikat o domenie; po prawej [Zbuduj podglad] (outline, tylko Astro)
// + przycisk glowny z primaryAction. Pasek postepu + blokada z bramek pod spodem.
// Podgladu w iframe NIE ma (usuniety 2026-09-22) — strona otwiera sie w nowej karcie.
// =============================================================================

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Zap, Loader2, ExternalLink, ShieldAlert, ArrowLeft, Eye } from "lucide-react";
import type { StaticSiteRow } from "@/services/staticSite/types";
import type { useMigrationRun } from "@/hooks/useMigrationRun";
import { listDeploys } from "@/services/staticSite/migrationRunner";
import { StaticSiteStatusBadge, engineLabel } from "./statusBadge";

interface Props {
  site: StaticSiteRow;
  run: ReturnType<typeof useMigrationRun>;
  mapName?: string | null;
}

export function StudioHeader({ site, run, mapName }: Props) {
  const isAstro = site.settings?.build_backend === "astro";
  const liveUrl = site.pages_subdomain ? `https://${site.pages_subdomain}` : null;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Ostatni udany deploy na branchu preview -> link „preview ↗"
  useEffect(() => {
    let alive = true;
    listDeploys(site.id, 12)
      .then((rows) => { if (alive) setPreviewUrl(rows.find((r) => r.branch === "preview" && r.status === "success" && r.cf_url)?.cf_url || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [site.id, site.last_deploy_id, site.updated_at, run.busy]);

  return (
    <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/strony" className="text-muted-foreground hover:text-foreground" title="Wszystkie strony"><ArrowLeft className="h-4 w-4" /></Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-lg font-semibold">{site.domain}</span>
            <StaticSiteStatusBadge status={site.status} />
            <span className="text-xs text-muted-foreground">{engineLabel(site)}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            {liveUrl && <a href={liveUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">pages.dev <ExternalLink className="h-3 w-3" /></a>}
            {previewUrl && <a href={previewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">preview <ExternalLink className="h-3 w-3" /></a>}
            {mapName && <Link to={`/mapy-tematyczne/${site.map_id}`} className="underline">mapa: {mapName}</Link>}
            <span>{site.status === "live" ? "Domena wskazuje na Cloudflare Pages." : "Domena nadal wskazuje na WordPress — przełączysz ją osobno."}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAstro && (
            <Button variant="outline" onClick={() => void run.runPreviewBuild()} disabled={run.busy !== null} title="Build na osobnym adresie preview.*.pages.dev — produkcja i domena nietknięte">
              {run.busy === "preview" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
              Zbuduj podgląd
            </Button>
          )}
          <Button onClick={() => void run.run()} disabled={run.busy !== null}>
            {run.busy === "deploy" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
            {run.primaryAction.label}
          </Button>
        </div>
      </div>
      {(run.busy || run.status) && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">{run.status}</div>
          {run.pct !== null && <Progress value={run.pct} className="h-1.5" />}
        </div>
      )}
      {run.blocked && (
        <div className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/5 p-2 text-xs">
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" /> <span>{run.blocked}</span>
        </div>
      )}
    </div>
  );
}
